import CapabilitySection from '@/components/site/CapabilitySection';
import { useLanguage } from '@/context/LanguageContext';
import { TID } from '@/lib/testIds';
import { IMAGES } from '@/lib/images';
import {
  Shield,
  Cube,
  Bomb,
  Truck,
  GasPump,
  Snowflake,
  Wrench,
  Mountains,
  Gear,
  Crosshair,
  Atom,
  Eye,
  Broadcast as Radar,
  Broadcast,
  Drone,
  Lightning,
  Stack,
  ShieldCheck,
  CheckSquare,
  ChartLine,
  Certificate as CertificateNato,
  Sparkle,
  Sword,
  Buildings,
  Detective,
  Cpu,
  ShieldStar,
  Lifebuoy,
  Radioactive,
  CheckCircle,
  BatteryCharging,
  Flask,
  ChartLineUp,
  Factory,
  ArrowsClockwise,
  Globe,
} from '@phosphor-icons/react';

// Icon mapping per section (order matches translation cards arrays)
const BALLISTIC_ICONS = [Shield, Cube, Bomb, ShieldCheck, Sparkle, Truck];
const MOBILITY_ICONS = [Truck, Gear, GasPump, Snowflake, Wrench, CheckSquare, Mountains, ChartLine];
const CBRNE_ICONS = [Radioactive, Broadcast, ShieldStar, Lifebuoy, Buildings, Detective, Cpu];
const AMMO_ICONS = [ShieldCheck, ChartLine, CheckSquare, Sparkle, Atom, Gear, CheckCircle];
const CUAS_ICONS = [Drone, Eye, Radar, Cpu, Lightning, Stack, Sword];
const BATTERY_ICONS = [BatteryCharging, Flask, ChartLineUp, Factory, ArrowsClockwise, Globe];

const attach = (cards, icons) => cards.map((c, i) => ({ ...c, Icon: icons[i] || Shield }));

export default function Capabilities() {
  const { t } = useLanguage();

  return (
    <>
      <CapabilitySection
        id="ballistic"
        testId={TID.ballistic.root}
        theme="light"
        imageSide="right"
        image={IMAGES.ballisticVehicle}
        imageAlt="Modern armored vehicle in engineering test facility"
        overline={t.ballistic.overline}
        title={t.ballistic.title}
        body={t.ballistic.body}
        cards={attach(t.ballistic.cards, BALLISTIC_ICONS)}
        ctaLabel={t.cta.learnMore}
        ctaTestId={TID.ballistic.cta}
        code="01"
      />

      <CapabilitySection
        id="mobility"
        testId={TID.mobility.root}
        theme="dark"
        imageSide="left"
        image={IMAGES.mobilityMaintenance}
        imageAlt="Ground vehicle maintenance and testing facility"
        overline={t.mobility.overline}
        title={t.mobility.title}
        body={t.mobility.body}
        body2={t.mobility.body2}
        cards={attach(t.mobility.cards, MOBILITY_ICONS)}
        ctaLabel={t.cta.learnMore}
        ctaTestId={TID.mobility.cta}
        code="02"
      />

      <CapabilitySection
        id="cbrne"
        testId={TID.cbrne.root}
        theme="light"
        imageSide="right"
        image={IMAGES.cbrneSpecialist}
        imageAlt="CBRNE specialist with detection equipment"
        overline={t.cbrne.overline}
        title={t.cbrne.title}
        body={t.cbrne.body}
        body2={t.cbrne.body2}
        cards={attach(t.cbrne.cards, CBRNE_ICONS)}
        ctaLabel={t.cta.learnMore}
        ctaTestId={TID.cbrne.cta}
        code="03"
      />

      <CapabilitySection
        id="ammo"
        testId={TID.ammo.root}
        theme="dark"
        imageSide="left"
        image={IMAGES.ammoLab}
        imageAlt="Ammunition testing and engineering laboratory"
        overline={t.ammo.overline}
        title={t.ammo.title}
        body={t.ammo.body}
        body2={t.ammo.body2}
        cards={attach(t.ammo.cards, AMMO_ICONS)}
        ctaLabel={t.cta.learnMore}
        ctaTestId={TID.ammo.cta}
        code="04"
      />

      <CapabilitySection
        id="cuas"
        testId={TID.cuas.root}
        theme="light"
        imageSide="right"
        image={IMAGES.cuasCommand}
        imageAlt="Anti-drone command center with radar interfaces"
        overline={t.cuas.overline}
        title={t.cuas.title}
        body={t.cuas.body}
        body2={t.cuas.body2}
        cards={attach(t.cuas.cards, CUAS_ICONS)}
        ctaLabel={t.cta.learnMore}
        ctaTestId={TID.cuas.cta}
        code="05"
      />

      <CapabilitySection
        id="battery"
        testId={TID.battery.root}
        theme="dark"
        imageSide="left"
        image={IMAGES.batteryLab}
        imageAlt="Battery cell testing and cycling laboratory"
        overline={t.battery.overline}
        title={t.battery.title}
        body={t.battery.body}
        body2={t.battery.body2}
        cards={attach(t.battery.cards, BATTERY_ICONS)}
        ctaLabel={t.cta.learnMore}
        ctaTestId={TID.battery.cta}
        code="06"
      />
    </>
  );
}
